import {
  addDays,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfWeek,
  endOfWeek,
  isSameDay,
  startOfYear,
  endOfYear,
  addYears,
  setDefaultOptions,
} from 'date-fns';

setDefaultOptions({ weekStartsOn: 1 });

const numericPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) =>
  Number(parts.find((part) => part.type === type)?.value);

/**
 * `now` as a local `Date` whose fields hold the wall-clock time in `timeZone`.
 *
 * The calendar renders `Date`s with the browser's offset, so a real instant would
 * draw on the wrong day whenever the viewer's offset differs from the site's.
 * Inverse of `convertToTimezone` in `@co4cloud/lib-utils`.
 */
export function zonedNow(timeZone?: string, now: Date = new Date()): Date {
  if (!timeZone) return now;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(now);

    return new Date(
      numericPart(parts, 'year'),
      numericPart(parts, 'month') - 1,
      numericPart(parts, 'day'),
      numericPart(parts, 'hour'),
      numericPart(parts, 'minute'),
      numericPart(parts, 'second'),
      now.getMilliseconds(),
    );
  } catch {
    // Unknown zone: draw the browser's calendar rather than throw.
    return now;
  }
}

/**
 * Preset ranges relative to `now` — pass a `zonedNow` result to build them around
 * a site's calendar day. Must stay a function: as a module-level object this froze
 * "today" at bundle load, so a tab open across midnight offered yesterday
 * (CO4-3535).
 */
export function getDefineds(now: Date = zonedNow()) {
  return {
    startOfWeek: startOfWeek(now),
    endOfWeek: endOfWeek(now),
    startOfLastWeek: startOfWeek(addDays(now, -7)),
    endOfLastWeek: endOfWeek(addDays(now, -7)),
    startOfToday: startOfDay(now),
    endOfToday: endOfDay(now),
    startOfYesterday: startOfDay(addDays(now, -1)),
    endOfYesterday: endOfDay(addDays(now, -1)),
    startOfMonth: startOfMonth(now),
    endOfMonth: endOfMonth(now),
    startOfLastMonth: startOfMonth(addMonths(now, -1)),
    endOfLastMonth: endOfMonth(addMonths(now, -1)),
    startOfLast3Months: startOfMonth(addMonths(now, -3)),
    endOfLast3Months: endOfMonth(addMonths(now, -1)),
    startOfThisYear: startOfYear(now),
    endOfThisYear: endOfYear(now),
    startOfLastYear: startOfYear(addYears(now, -1)),
    endOfLastYear: endOfYear(addYears(now, -1)),
  };
}

/**
 * @deprecated Use `getDefineds()`. Kept so code written against the old API still
 * compiles. Backed by a getter rather than a literal, so it cannot freeze "today"
 * at bundle load the way the original object did (CO4-3535).
 */
export const defineds = new Proxy({} as ReturnType<typeof getDefineds>, {
  get: (_target, key) => getDefineds()[key as keyof ReturnType<typeof getDefineds>],
});

const staticRangeHandler = {
  range: {},
  isSelected(range) {
    const definedRange = this.range();
    return isSameDay(range.startDate, definedRange.startDate) && isSameDay(range.endDate, definedRange.endDate);
  },
};

export function createStaticRanges(ranges) {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
}

/**
 * Display format per app language (CO4-3594). Two explicit, unrelated dialects -
 * `display` is date-fns's token style (`dd`, `MM`, `yyyy`), used by `format`/`parse`.
 * `mask` is IMask's own pattern syntax: single-letter block names (`d`, `m`, `Y` -
 * each a whole multi-digit block, not one letter per digit), literals escaped in
 * `{}`, and a backtick between blocks of differing length so it can tell where one
 * ends and the next begins. `d{.}`m{.}`Y` is IMask's own built-in default.
 */
const DATE_FORMATS: Record<string, { display: string; mask: string }> = {
  de: { display: 'dd.MM.yyyy', mask: 'd{.}`m{.}`Y' },
  en: { display: 'MM/dd/yyyy', mask: 'm{/}`d{/}`Y' },
};

export function localeDateFormat(lang: string) {
  return DATE_FORMATS[lang] ?? DATE_FORMATS.en;
}

/**
 * `react-date-range`'s locale export has no `en` key (only `enUS`/`enGB`) - an
 * unmapped lookup silently resolves to `undefined`, which is fine for month names
 * (the calendar falls back quietly) but throws once something reads `.formatLong`
 * off it. Mapped explicitly so `DateTextInput`'s date-fns calls always get a real
 * locale object.
 */
export const RDR_LOCALE_KEY: Record<string, string> = { en: 'enUS', de: 'de' };
